import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  combineLatest,
  NEVER,
  Observable,
  of,
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
} from 'rxjs';

import { BookingHeaderSummary, GlobalBookingTripDates } from '@fcom/common/interfaces';
import { finShare, safeMap, snapshot } from '@fcom/rx';
import { GtmService } from '@fcom/common/gtm';
import { loginStatus, profileName, profileType } from '@fcom/core/selectors';
import { LoginStatus, ProfileType } from '@fcom/core-api/login';
import {
  CmsPageService,
  ElementActions,
  ElementTypes,
  GaContext,
  isNavigationEvent,
  NavigationMenuService,
  PageMetaService,
} from '@fcom/common';
import { LoginDialogComponent, LoginWidgetContext } from '@fcom/common/login';
import { Category, FinnairOrder } from '@fcom/dapi/api/models';
import { categoryPartStatus, orderData, OrderPartStatus } from '@fcom/common-booking';
import { AppState, ConfigService, isFalsy, NativeBridgeService, RootPaths, TzDate } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { unsubscribe } from '@fcom/core/utils';
import { CmsTemplate } from '@fcom/core-api';
import {
  bookingHasDisruption,
  getTripTypeFromBookingLocationCodes,
} from '@fcom/common-booking/utils/common-booking.utils';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';

import { getStage } from '../../store';
import { MmbStage } from '../../enums';

const CTA_VISIBLE_CLASS = 'cta-visible';

@Component({
  selector: 'fin-mmb-routing',
  templateUrl: './manage-booking-routing.component.html',
  styleUrls: ['./manage-booking-routing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingRoutingComponent implements OnInit, OnDestroy {
  readonly LoginStatus = LoginStatus;
  readonly LoginWidgetContext = LoginWidgetContext;
  readonly MmbStage = MmbStage;

  loginStatus$: Observable<LoginStatus>;
  profileName$: Observable<string>;
  profileType$: Observable<ProfileType>;
  isShowLoginHeader$: Observable<boolean>;
  stage$: Observable<MmbStage>;
  booking$: Observable<FinnairOrder>;
  homeUrl: string;
  hasBasket$: Observable<boolean>;
  isBasketDisabled$: Observable<boolean>;
  hasTwoColumnLayout$: Observable<boolean>;
  isReviewStage$: Observable<boolean>;
  template$: Observable<CmsTemplate>;
  title$: Observable<string>;
  isNavigationMenuOpen$: Observable<boolean>;
  hasUnpaidServices$: Observable<boolean>;
  bookingSummary$: Observable<BookingHeaderSummary>;
  bookingIsDisrupted$: Observable<boolean>;
  viewIsNotCheckinInsideApp$: Observable<boolean>;
  enableExpediaWidget: boolean;

  @ViewChild('loginDialog', { static: false }) loginDialog: LoginDialogComponent;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private store$: Store<AppState>,
    private languageService: LanguageService,
    private navigationMenuService: NavigationMenuService,
    private gtmService: GtmService,
    private pageMetaService: PageMetaService,
    private configService: ConfigService,
    private router: Router,
    private pageService: CmsPageService,
    private feedbackService: FeedbackService,
    @Inject(DOCUMENT) private readonly document: Document,
    private renderer: Renderer2,
    public nativeBridgeService: NativeBridgeService,
    @Inject(PLATFORM_ID) private platform: object
  ) {}

  ngOnInit(): void {
    this.loginStatus$ = this.store$.pipe(loginStatus());
    this.profileName$ = this.store$.pipe(profileName());
    this.profileType$ = this.store$.pipe(profileType());
    this.stage$ = this.store$.pipe(getStage());
    this.booking$ = this.store$.pipe(orderData());

    this.viewIsNotCheckinInsideApp$ = this.stage$.pipe(
      map((stage) =>
        isFalsy(MmbStage.CHECK_IN_FLOW === stage && this.nativeBridgeService.isInsideNativeWebView === true)
      ),
      distinctUntilChanged(),
      finShare()
    );

    if (isPlatformBrowser(this.platform)) {
      this.hasUnpaidServices$ = this.booking$.pipe(map((booking) => booking?.services?.unpaid?.length > 0));
      this.isNavigationMenuOpen$ = this.navigationMenuService.isNavigationMenuOpen$;

      this.bookingSummary$ = this.booking$.pipe(
        map((order) => {
          const locations = order.bounds.map((bound) => ({
            originLocationCode: bound.departure.locationCode,
            destinationLocationCode: bound.arrival.locationCode,
          }));

          const dates: GlobalBookingTripDates = {
            departureDate: TzDate.of(order.bounds[0].departure.dateTime).toLocalDate(),
            ...(order.bounds.length > 1
              ? { returnDate: TzDate.of(order.bounds[order.bounds.length - 1].arrival.dateTime).toLocalDate() }
              : {}),
          };

          return {
            tripType: getTripTypeFromBookingLocationCodes(locations),
            locations,
            paxAmount: order.passengers.length,
            dates,
          };
        })
      );
      this.bookingIsDisrupted$ = this.booking$.pipe(
        map((booking: FinnairOrder) => bookingHasDisruption(booking)),
        finShare()
      );
    } else {
      this.hasUnpaidServices$ = of(false);
      this.isNavigationMenuOpen$ = of(false);
      this.bookingSummary$ = NEVER;
      this.bookingIsDisrupted$ = of(false);
    }

    const priceUpdating$: Observable<boolean> = this.store$.pipe(
      categoryPartStatus(Category.BAGGAGE),
      map((status) => status === OrderPartStatus.UPDATING),
      startWith(false)
    );

    this.isShowLoginHeader$ = this.stage$.pipe(map((stage) => MmbStage.LOGIN === stage));
    this.isReviewStage$ = this.stage$.pipe(map((stage) => MmbStage.REVIEW === stage));

    this.hasTwoColumnLayout$ = this.stage$.pipe(
      map((stage) =>
        [MmbStage.VIEW, MmbStage.PASSENGERS, MmbStage.TRAVEL_PREPARATION, MmbStage.CAR_RENTAL].includes(stage)
      )
    );
    this.homeUrl = this.languageService.langValue ? `/${this.languageService.langValue}` : '/en';
    this.hasBasket$ = this.stage$.pipe(
      map((stage) => ![MmbStage.REVIEW, MmbStage.CHECKOUT, MmbStage.CONFIRMATION].includes(stage))
    );
    this.isBasketDisabled$ = combineLatest([priceUpdating$, this.hasUnpaidServices$]).pipe(
      map(([priceUpdating, hasUnpaidServices]) => !hasUnpaidServices || priceUpdating),
      startWith(true)
    );
    this.template$ = this.router.events.pipe(
      filter(isNavigationEvent),
      startWith(this.router),
      distinctUntilChanged(),
      filter((event: NavigationEnd) =>
        event.url
          ?.split('?')[0]
          .split('/')
          .slice(2)
          .some((path) =>
            [
              MANAGE_BOOKING_STEPS.VIEW.path,
              MANAGE_BOOKING_STEPS.VIEW_PASSENGERS.path,
              MANAGE_BOOKING_STEPS.TRAVEL_PREPARATION.path,
              MANAGE_BOOKING_STEPS.CAR_RENTAL.path,
            ].includes(path)
          )
      ),
      switchMap(() => {
        return this.pageService.getPageData(this.router.url);
      }),
      finShare()
    );

    this.title$ = this.template$.pipe(safeMap((template: CmsTemplate) => template?.header?.[0]?.title ?? ''));

    this.subscriptions.add(
      this.stage$
        .pipe(
          distinctUntilChanged((prevStage, curStage) => prevStage === curStage),
          filter((stage) => stage !== MmbStage.LOGIN),
          switchMap((stage) => this.languageService.translate(`MMB.meta.${stage}.title`))
        )
        .subscribe((title) => {
          this.pageMetaService.setTitle(`${title} | Finnair`);
        })
    );

    this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
    this.feedbackService.setFeedbackWidgetVisibility(true);
    this.enableExpediaWidget = this.configService.cfg.enableExpediaWidget;
  }

  ngOnDestroy(): void {
    this.feedbackService.setFeedbackWidgetVisibility(false);
    unsubscribe(this.subscriptions);
    this.renderer.removeClass(this.document.body, CTA_VISIBLE_CLASS);
  }

  openBasket(): void {
    this.gtmService.trackElement(
      'continue-to-review_header-basket',
      GaContext.MANAGE_BOOKING,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK,
      `recLoc-${snapshot(this.booking$).otherInformation.analyticsToken}`
    );

    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.REVIEW.path}`
    );
  }

  toggleMenu(): void {
    this.navigationMenuService.toggleMenuOpen();
  }

  navigateBackFromReview(): void {
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW.path}`
    );
  }

  onHomeClick(url: string): void {
    this.router.navigateByUrl(url);
  }

  trackCTAButton(): void {
    const ctaElement = this.document.querySelector('fcom-cta-button .visible');
    if (ctaElement && !this.document.body.classList.contains(CTA_VISIBLE_CLASS)) {
      this.renderer.addClass(this.document.body, CTA_VISIBLE_CLASS);
    } else if (!ctaElement && this.document.body.classList.contains(CTA_VISIBLE_CLASS)) {
      this.renderer.removeClass(this.document.body, CTA_VISIBLE_CLASS);
    }
  }

  removeCTAButtonClass(): void {
    this.renderer.removeClass(this.document.body, CTA_VISIBLE_CLASS);
  }
}
